'use client';
import React, { useState, useEffect } from 'react';
import { CategoryProducts as SharedCategoryProducts } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { getRegion } from '@/lib/data/regions';
import TemplateProductCard from '@/modules/products/components/product-preview/template-product-card';
import type { CategoryProductsProps, CategoryProduct } from '@launchstore/shared-puck';

// Wrapper fetches storefront products for the configured categoryId and
// passes them as a projected CategoryProduct[] to the shared
// presentational component.
//
// Card rendering goes through the merchant's designed PRODUCT_CARD template
// (TemplateProductCard) instead of the shared default card, matching
// FeaturedProducts and the product grids.
//
// The shared "View All" button hardcodes /categories/{categoryId} — no
// countryCode prefix and an ID where the storefront route expects a handle —
// so we hide it and render our own button with the correct URL.
const SharedCategoryProductsRender = (SharedCategoryProducts as any).render;

const BTN_RADIUS: Record<string, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

const CategoryProductsImpl: React.FC<CategoryProductsProps> = (rawProps: any) => {
  const props = rawProps as CategoryProductsProps;
  const { context } = usePuckContext() as any;
  const cardTemplate = context?.productCardTemplate;
  const [rawById, setRawById] = useState<Record<string, any>>({});
  const [products, setProducts] = useState<CategoryProduct[]>([]);
  const [region, setRegion] = useState<any>(null);
  const [categoryHref, setCategoryHref] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const countryCode = context?.countryCode
    || (typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : '') || 'us';

  useEffect(() => {
    let active = true;
    async function fetchAll() {
      if (!props.categoryId) { setLoading(false); return; }
      setLoading(true); setError('');
      try {
        const params = new URLSearchParams({
          category_id: props.categoryId,
          limit: String(props.maxProducts || 12),
          // Page country → correct region for pricing; /api/products falls
          // back to the store's first region when it doesn't match
          countryCode,
        });
        const [res, region, cres] = await Promise.all([
          fetch(`/api/products?${params}`),
          // Region via the standard data layer — the /api/region endpoint
          // this used to call does not exist, which silently killed template
          // card rendering (region never resolved).
          getRegion(countryCode).catch(() => null),
          // Resolve the category handle for the View All link (the
          // storefront category route is /:countryCode/categories/:handle)
          fetch(`/${countryCode}/api/categories`).catch(() => null),
        ]);
        const data = await res.json();
        const raw: any[] = data.products || [];
        if (!active) return;
        const projected: CategoryProduct[] = raw.map((p: any) => ({
          id: p.id, title: p.title, handle: p.handle,
          thumbnail: p.thumbnail || p.images?.[0]?.url,
          price: p.variants?.[0]?.calculated_price?.calculated_amount ?? null,
          compareAtPrice: p.variants?.[0]?.compare_at_price ?? null,
          rating: p.rating ?? null,
          isNew: !!p.created_at && (Date.now() - new Date(p.created_at).getTime()) < 30 * 24 * 3600 * 1000,
          onSale: !!p.variants?.[0]?.compare_at_price && (p.variants[0].calculated_price?.calculated_amount ?? 0) < p.variants[0].compare_at_price,
        }));
        setProducts(projected);
        setRawById(Object.fromEntries(raw.map((p: any) => [p.id, p])));

        if (active && region) setRegion(region);
        if (cres && cres.ok) {
          const cdata = await cres.json().catch(() => null);
          const match = (cdata?.product_categories || []).find((c: any) => c.id === props.categoryId);
          if (active && match?.handle) setCategoryHref(`/${countryCode}/categories/${match.handle}`);
        }
      } catch (err: any) {
        if (active) setError(err?.message || 'Failed to fetch products');
      } finally {
        if (active) setLoading(false);
      }
    }
    fetchAll();
    return () => { active = false; };
  }, [props.categoryId, props.maxProducts, countryCode]);

  return (
    <>
      <SharedCategoryProductsRender
        {...props}
        // Shared button links to /categories/{id} (broken) — ours renders below
        showViewAllButton={false}
        products={products}
        loading={loading}
        error={error}
        renderProduct={
          cardTemplate && region
            ? (p: any) => {
                const raw = rawById[p.id];
                if (!raw) return null;
                return (
                  <TemplateProductCard
                    product={raw}
                    region={region}
                    productCardTemplate={cardTemplate}
                    countryCode={countryCode}
                  />
                );
              }
            : undefined
        }
      />
      {props.showViewAllButton && categoryHref && (
        <div className="text-center mt-12">
          <a
            href={categoryHref}
            className={`inline-block px-8 py-3 transition-colors ${BTN_RADIUS[props.buttonRadius as string] || 'rounded-md'}`}
            style={{ backgroundColor: props.buttonColor || '#000000', color: props.buttonTextColor || '#ffffff' }}
          >
            {props.viewAllButtonText || 'View All Products'}
          </a>
        </div>
      )}
    </>
  );
};

export const CategoryProducts: any = { ...SharedCategoryProducts, render: (props: any) => <CategoryProductsImpl {...props} /> };
export default CategoryProducts;
